// external
import axios from 'axios';

// libraries
import { getCurrentAxiosRequestInterceptor } from '@makemydeal/dr-shared-network';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import type { ScopedStore } from '@makemydeal/shared-scoped-store';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// selectors
import { getCustomizablePdfPayload } from '../selectors/printPdfDealSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';

// actions
import { printPdfLoading, printPdfSuccess, setPdfUrlForSharing } from '../actions/printPdfActionCreator';
import { saveOffer } from '../actions/offerActionCreators';

// services
import { ensurePrintingLogo } from './ensurePrintingLogo';

// interfaces/types
import type { DashStore } from '@makemydeal/dr-shared-store';
import type { ForSharing, ScopeGridSelections } from '../types/types';

const ENDPOINT_PATH = '/generateCustomizablePdf';

/**
 * Service to call the customizable PDF generation endpoint
 * @param store - Redux store
 * @param forSharing - Whether the PDF is for sharing ('text', 'email') or direct viewing (false)
 * @param selectedProposalId - Optional proposal ID for customizable PDFs
 */
export const callPdfService = async (
    store: DashStore,
    forSharing: ForSharing = false,
    selectedProposalId?: string,
    selectedScopeIds?: string[],
    opts: {
        isDraftSave?: boolean;
        suppressOutput?: boolean;
        skipSave?: boolean;
        scopeGridSelections?: ScopeGridSelections;
        saveFailed?: boolean;
        originalAction?: AnyFSA;
    } = {}
) => {
    const {
        isDraftSave = true,
        suppressOutput = false,
        skipSave = false,
        scopeGridSelections,
        saveFailed = false,
        originalAction
    } = opts;

    // MUST run before the payload is built: dealerLogo is read from state, not passed in.
    await ensurePrintingLogo(store);

    const realState = (store as ScopedStore).getRealState();
    const scopedState = store.getState();

    let pdfPayload = getCustomizablePdfPayload(realState, selectedScopeIds, scopeGridSelections);

    // Add proposalId if provided
    if (selectedProposalId) {
        pdfPayload = {
            ...pdfPayload,
            proposalId: selectedProposalId
        };
    }

    // Add sharing context
    pdfPayload = { ...pdfPayload, sharingType: forSharing || 'print' };

    const locHref = locHrefUtil.getLocHref();

    // short URL when sharing; saveFailed so the BFF/Lambda logs a PDF generated despite a failed version save.
    const params = [forSharing && 'generateShortUrl=true', saveFailed && 'saveFailed=true'].filter(Boolean).join('&');
    const queryParams = params ? `?${params}` : '';
    const endpoint = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, ENDPOINT_PATH + queryParams).url;

    const axiosRequestInterceptor = getCurrentAxiosRequestInterceptor();
    const { headers } = await axiosRequestInterceptor({ url: endpoint });

    const startTime = performance.now();
    const nrEventName = 'MV:PrintGenerateCustomizablePdf';

    try {
        if (!skipSave && !offerReduxSelectors.getIsDealXgRecordMissed(scopedState)) {
            store.dispatch(saveOffer({ isDraft: isDraftSave, originalAction }));
        }

        store.dispatch(printPdfLoading(true, forSharing, suppressOutput));

        const response = await axios.post(endpoint, pdfPayload, {
            headers
        });

        const pdfUrl = response.data.data.url;

        logNewRelicPageAction(nrEventName, {
            durationMs: performance.now() - startTime,
            result: 'success',
            endpoint: ENDPOINT_PATH,
            forSharing: forSharing || 'none',
            hasProposalId: !!selectedProposalId
        });

        if (forSharing) {
            store.dispatch(setPdfUrlForSharing(pdfUrl, forSharing));
        } else if (!suppressOutput) {
            window.open(pdfUrl, '_blank')?.focus();
        }

        store.dispatch(printPdfSuccess());
    } catch (err) {
        logNewRelicPageAction(nrEventName, {
            durationMs: performance.now() - startTime,
            result: 'error',
            endpoint: ENDPOINT_PATH,
            forSharing: forSharing || 'none',
            hasProposalId: !!selectedProposalId
        });
        store.dispatch(printPdfLoading(false, false));
    }
};
